import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { AdRulesContent } from "@/components/legal/AdRulesContent";
import { useLocale } from "@/lib/i18n/locale-context";
import { getLegalCopy } from "@/lib/i18n/legal-copy";

export type LegalDoc = "integritet" | "cookies" | "villkor" | "regler" | null;

const CONTACT = "hej@sellavo.com";
const UPDATED = "31 augusti 2026";
/** Bolaget som driver Sellavo. */
export const COMPANY = {
  name: "Anno One Corporation Ltd",
  addressLines: ["Anno One Corporation Ltd", "Larnaca", "Cypern"],
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="font-heading text-base font-semibold text-foreground">{title}</h3>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <div className="mt-3 overflow-hidden rounded-md border border-border">
      <table className="w-full border-collapse text-left text-xs">
        <thead className="bg-muted/60">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 font-medium text-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border align-top">
              {r.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-muted-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrivacyPolicy() {
  const { lang } = useLocale();
  if (lang !== "sv") return <TranslatedLegalBody copy={getLegalCopy(lang, "privacy")} />;
  return (
    <div>
      <p className="text-xs text-muted-foreground">Senast uppdaterad: {UPDATED}</p>

      <Section title="1. Om den här policyn">
        <p>
          Sellavo är en tjänst där du fotar en pryl, får ett annonsförslag skrivet av AI och kan
          publicera annonsen så att andra kan hitta och kontakta dig. Den här policyn beskriver
          vilka personuppgifter vi behandlar, varför vi gör det, vilken rättslig grund vi stödjer
          oss på och vilka rättigheter du har enligt dataskyddsförordningen (GDPR).
        </p>
        <p>
          Sellavo drivs av {COMPANY.name}, {COMPANY.addressLines.slice(1).join(", ")}, som är
          personuppgiftsansvarig. Frågor om personuppgifter besvaras på{" "}
          <span className="text-foreground">{CONTACT}</span>.
        </p>
      </Section>

      <Section title="2. Uppgifter vi behandlar">
        <p>
          <strong className="text-foreground">Kontouppgifter.</strong> E-postadress och ett lösenord
          som lagras i hashad form. Loggar du in med Google får vi de grunduppgifter Google delar
          vid inloggningen, i praktiken e-postadress och konto-ID.
        </p>
        <p>
          <strong className="text-foreground">Annonsinnehåll.</strong> Bilder du laddar upp, titel,
          beskrivning, pris, kategori, ort och eventuell gatuadress eller områdesangivelse, samt de
          kontaktuppgifter du själv väljer att publicera (namn, e-post, telefonnummer).
        </p>
        <p>
          <strong className="text-foreground">Platsuppgifter.</strong> Den ort eller adress du anger
          omvandlas till ungefärliga koordinater så att annonsen kan visas på en karta. Vi hämtar
          ingen plats från din enhet.
        </p>
        <p>
          <strong className="text-foreground">Teknisk information.</strong> Uppgifter som uppstår
          när tjänsten används, till exempel sessionsdata för inloggning samt säkerhets- och
          felloggar hos våra leverantörer.
        </p>
        <p>
          Vi ber dig inte om, och vill inte ha, känsliga personuppgifter (till exempel uppgifter om
          hälsa, religion eller politisk uppfattning) i annonser eller bilder.
        </p>
      </Section>

      <Section title="3. Ändamål och rättslig grund">
        <Table
          head={["Vad vi gör", "Rättslig grund"]}
          rows={[
            ["Skapa och administrera ditt konto samt hålla dig inloggad", "Avtal (art. 6.1 b)"],
            ["Analysera din bild med AI och skapa förslag på text och pris", "Avtal (art. 6.1 b)"],
            [
              "Publicera och visa din annons, inklusive kontaktuppgifter du valt",
              "Avtal (art. 6.1 b)",
            ],
            ["Omvandla angiven ort/adress till koordinater för kartan", "Avtal (art. 6.1 b)"],
            [
              "Skydda tjänsten mot missbruk, spam och tekniska fel",
              "Berättigat intresse (art. 6.1 f)",
            ],
            ["Valfri besöksstatistik via analyscookies", "Samtycke (art. 6.1 a)"],
          ]}
        />
      </Section>

      <Section title="4. AI-behandling av bild och text">
        <p>
          När du väljer att generera en annons skickas den uppladdade bilden och dina eventuella
          instruktioner till vår AI-leverantör, som returnerar förslag på titel, beskrivning och
          pris. Förslaget är just ett förslag — du kan ändra allt innan du publicerar, och du
          ansvarar för att den publicerade texten stämmer.
        </p>
        <p>
          Vi använder inte ditt innehåll för att bygga egna AI-modeller. Vilka villkor leverantören
          i sin tur tillämpar framgår av avtalet med dem; vi väljer leverantörer som behandlar
          innehållet för att leverera svaret och inte för egen modellträning.
        </p>
      </Section>

      <Section title="5. Mottagare och personuppgiftsbiträden">
        <p>Vi säljer aldrig dina uppgifter. För att kunna leverera tjänsten anlitar vi:</p>
        <Table
          head={["Typ av mottagare", "Vad de gör"]}
          rows={[
            ["Hosting och databas", "Drift av webbplats, konton, databas och bildlagring"],
            ["AI-leverantör", "Analyserar bilden och genererar annonsförslaget"],
            ["Karttjänst", "Omvandlar adress till koordinater och visar kartan"],
            [
              "Google (endast vid Google-inloggning)",
              "Autentiserar dig och bekräftar din e-postadress",
            ],
          ]}
        />
        <p>
          Vi kan också behöva lämna ut uppgifter om lag, myndighetsbeslut eller domstol kräver det.
          Behandling kan ske utanför EU/EES; i så fall sker överföringen med stöd av
          EU-kommissionens standardavtalsklausuler eller ett beslut om adekvat skyddsnivå.
        </p>
      </Section>

      <Section title="6. Publicerade annonser är offentliga">
        <p>
          En publicerad annons är öppen för alla med länken: bilder, text, pris, kategori, ort och
          de kontaktuppgifter du angett. Sidan kan indexeras av sökmotorer och kan finnas kvar i
          deras cache en tid även efter att du raderat annonsen. Publicera därför bara uppgifter du
          är bekväm med att visa öppet — det räcker ofta med förnamn och en e-postadress.
        </p>
      </Section>

      <Section title="7. Hur länge vi sparar">
        <Table
          head={["Uppgift", "Lagringstid"]}
          rows={[
            ["Annonser och tillhörande bilder", "Tills du raderar annonsen eller ditt konto"],
            [
              "Kontouppgifter",
              "Så länge kontot finns. Begär du borttagning döljs allt direkt och raderas 30 dagar senare, så att du hinner ångra dig",
            ],
            [
              "Logg över begärda kontoborttagningar",
              "Sparas separat i tre år som underlag för att visa att din begäran hanterades",
            ],
            [
              "Kvitton och underlag för betalningar",
              "Sju år, eftersom bokföringslagen kräver det. De finns kvar även efter att kontot raderats",
            ],
            [
              "Säkerhets- och felloggar",
              "Kort tid hos våra leverantörer, normalt högst några månader",
            ],
            [
              "Ditt cookieval",
              "Sparas i din webbläsare, och på ditt konto om du är inloggad, tills du ändrar det",
            ],

          ]}
        />
      </Section>

      <Section title="8. Säkerhet">
        <p>
          Trafiken skyddas med TLS, lösenord lagras hashade och åtkomsten till annonsdata styrs av
          regler på databasnivå så att bara du kan ändra dina egna annonser. Ingen tjänst kan
          garantera hundraprocentigt skydd, men vi arbetar löpande med att hålla uppgifterna säkra
          och begränsa vilka som kan komma åt dem.
        </p>
      </Section>

      <Section title="9. Dina rättigheter">
        <p>
          Du har rätt att begära tillgång till dina uppgifter, rättelse, radering, begränsning av
          behandlingen och dataportabilitet. Du har rätt att invända mot behandling som sker med
          stöd av berättigat intresse, och du kan när som helst återkalla ett samtycke — till
          exempel ditt cookieval — utan att det påverkar behandling som redan skett.
        </p>
        <p>
          Mycket kan du göra själv: redigera eller radera annonser under ”Mina annonser”, byta
          lösenord, och ändra cookieval via länken i sidfoten. För övriga begäranden hör av dig till{" "}
          <span className="text-foreground">{CONTACT}</span> — vi svarar normalt inom en månad.
        </p>
      </Section>

      <Section title="10. Klagomål">
        <p>
          Om du anser att vi behandlar dina personuppgifter felaktigt får du gärna kontakta oss
          först. Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY), Box
          8114, 104 20 Stockholm, imy.se.
        </p>
      </Section>

      <Section title="11. Mobilappen för iOS och Android">
        <p>
          Använder du Sellavo som app i telefonen behandlar vi samma uppgifter som på webben, plus
          det som appen behöver för att fungera:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <span className="text-foreground">Kamera och bildbibliotek</span> – bara när du själv
            väljer att fota eller välja en bild till en annons. Vi läser aldrig bildbiblioteket i
            bakgrunden.
          </li>
          <li>
            <span className="text-foreground">Mikrofon</span> – enbart som ljudspår i film du
            spelar in till en annons.
          </li>
          <li>
            <span className="text-foreground">Plats</span> – används vid enstaka tillfällen för att
            föreslå ort och visa prylar nära dig. Vi hämtar aldrig position i bakgrunden och sparar
            inte exakta koordinater.
          </li>
          <li>
            <span className="text-foreground">Notistoken</span> – ett tekniskt ID från Apple eller
            Google så att en notis hittar rätt enhet. Det raderas när du loggar ut eller stänger av
            notiser.
          </li>
          <li>
            <span className="text-foreground">Enhetsinformation</span> – modell, operativsystem och
            appversion, för felsökning och support.
          </li>
          <li>
            <span className="text-foreground">Face ID / Touch ID</span> – om du slår på applåset
            sker kontrollen i telefonen. Vi får aldrig se eller lagra din biometri.
          </li>
        </ul>
        <p className="mt-2">
          Appen använder inte annonsspårning, mäter dig inte i andra appar eller på andra sajter och
          har inget reklam-ID. Du väljer statistik och utskick vid första starten och kan ändra
          valet under Mitt konto. Behörigheter kan alltid dras tillbaka i telefonens inställningar –
          då stängs den funktionen av, men resten av appen fortsätter fungera.
        </p>
        <p className="mt-2">
          Vill du radera kontot gör du det direkt i appen under Mitt konto, längst ner under
          Farozonen. Kontot och dina annonser tas bort enligt tiderna i avsnitt 7.
        </p>
      </Section>

      <Section title="12. Ändringar">
        <p>
          Vi kan uppdatera den här policyn när tjänsten förändras. Den senaste versionen finns
          alltid här, med datum högst upp. Vid väsentliga ändringar informerar vi i tjänsten.
        </p>
      </Section>
    </div>
  );
}

export function CookiePolicy() {
  const { lang } = useLocale();
  if (lang !== "sv") return <TranslatedLegalBody copy={getLegalCopy(lang, "cookies")} />;
  return (
    <div>
      <p className="text-xs text-muted-foreground">Senast uppdaterad: {UPDATED}</p>

      <Section title="1. Vad policyn gäller">
        <p>
          Policyn beskriver de cookies och liknande tekniker — som localStorage i din webbläsare —
          som Sellavo använder, vad de gör och hur du styr dem. Cookies är små textfiler som sparas
          på din enhet när du besöker en webbplats.
        </p>
        <p>
          Nödvändiga cookies sätts direkt eftersom sajten inte fungerar utan dem. Övriga tekniker
          sätts bara om du godkänner dem i cookierutan.
        </p>
      </Section>

      <Section title="2. Nödvändiga cookies och lagring">
        <Table
          head={["Namn/typ", "Syfte", "Lagringstid"]}
          rows={[
            [
              "Sessionscookie för inloggning",
              "Håller dig inloggad mellan sidvisningar",
              "Sessionen, förnyas löpande",
            ],
            [
              "Autentiseringstoken (localStorage)",
              "Identifierar ditt konto i webbläsaren",
              "Tills du loggar ut",
            ],
            [
              "sellavo-cookie-consent",
              "Kommer ihåg ditt cookieval (kopplas till ditt konto om du är inloggad)",
              "Tills du ändrar det",
            ],
            [
              "sellavo:utkast (localStorage)",
              "Sparar ditt påbörjade annonsutkast lokalt",
              "Tills annonsen publiceras eller rensas",
            ],
          ]}
        />
        <p>
          De här går inte att stänga av i cookierutan, eftersom inloggning, publicering och
          grundläggande säkerhet slutar fungera utan dem. Du kan alltid rensa dem i webbläsarens
          inställningar.
        </p>
      </Section>

      <Section title="3. Analyscookies (valfria)">
        <p>
          Om du godkänner analys kan vi mäta aggregerad och anonymiserad användning — till exempel
          hur många som startar och slutför en annons — för att förbättra tjänsten. Vi använder inte
          uppgifterna för annonsspårning, profilering eller försäljning till tredje part, och de
          sätts inte förrän du sagt ja.
        </p>
        <p>
          Väljer du ”Endast nödvändiga” sätts inga analyscookies alls. Ditt val sparas och gäller
          tills du ändrar det – är du inloggad följer valet med till dina andra enheter.
        </p>
      </Section>

      <Section title="4. Tredjepartscookies">
        <p>
          Loggar du in med Google kan Google sätta egna cookies som en del av inloggningsflödet. De
          styrs av Googles egen cookie- och integritetspolicy. Kartan hämtar kartbilder från en
          extern karttjänst, som kan logga tekniska uppgifter som IP-adress för att kunna leverera
          bilderna.
        </p>
      </Section>

      <Section title="5. Ändra eller återkalla ditt val">
        <p>
          Klicka på ”Cookieinställningar” längst ner på sidan för att öppna rutan igen och ändra
          ditt val när som helst. Du kan också blockera eller radera cookies direkt i webbläsaren —
          då kan inloggningen och sparade utkast sluta fungera.
        </p>
      </Section>

      <Section title="6. Cookies och personuppgifter">
        <p>
          Uppgifter som samlas via cookies kan i vissa fall utgöra personuppgifter. Hur vi behandlar
          dem, och vilka rättigheter du har, beskrivs i integritetspolicyn.
        </p>
      </Section>
    </div>
  );
}

export function TermsOfUse() {
  const { lang } = useLocale();
  if (lang !== "sv") return <TranslatedLegalBody copy={getLegalCopy(lang, "terms")} />;
  return (
    <div>
      <p className="text-xs text-muted-foreground">Senast uppdaterad: {UPDATED}</p>

      <Section title="1. Tjänsten">
        <p>
          Sellavo hjälper dig att skapa och publicera en annons. Vi är en plattform för annonser —
          vi är inte part i affären mellan säljare och köpare, hanterar inga betalningar och
          ansvarar inte för prylen, leveransen eller uppgörelsen mellan er.
        </p>
        <p>
          Sellavo drivs av {COMPANY.name}, {COMPANY.addressLines.slice(1).join(", ")}. Du når oss på{" "}
          <span className="text-foreground">{CONTACT}</span>.
        </p>
        <p>
          Betalning, kvitto och eventuella tvister sköter köpare och säljare direkt med varandra.
          Vi erbjuder ingen köpgaranti och kan inte hålla pengar mellan parterna.
        </p>
      </Section>

      <Section title="2. Ditt konto">
        <p>
          Du ansvarar för att uppgifterna du anger stämmer och för att hålla ditt lösenord hemligt.
          Du måste vara minst 18 år, eller ha målsmans tillstånd, för att skapa konto och publicera
          annonser.
        </p>
      </Section>

      <Section title="3. Ditt annonsinnehåll">
        <p>
          Du ansvarar för allt du publicerar, även när texten är genererad av AI — läs igenom och
          rätta förslaget innan du publicerar. Du måste ha rätt att sälja prylen och att använda
          bilderna du laddar upp.
        </p>
        <p>Det är inte tillåtet att annonsera eller publicera:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>prylar eller tjänster som är olagliga eller kräver tillstånd du inte har</li>
          <li>vapen, ammunition, droger, läkemedel, tobak, alkohol eller stöldgods</li>
          <li>levande djur, kroppsdelar eller sexuella tjänster</li>
          <li>
            vilseledande, bedrägliga eller uppenbart felaktiga uppgifter, inklusive falska priser
          </li>
          <li>andras bilder, texter eller varumärken utan tillåtelse</li>
          <li>andra personers personuppgifter eller bilder utan deras samtycke</li>
          <li>kränkande, hotfullt, pornografiskt, hatiskt eller diskriminerande innehåll</li>
          <li>spam, kedjebrev, upprepade dubbletter av samma annons eller ren reklam</li>
        </ul>
        <p>
          Du ger oss en icke-exklusiv, avgiftsfri rätt att lagra, visa, beskära, komprimera och
          sprida ditt annonsinnehåll i tjänsten så länge annonsen är publicerad, och att visa utdrag
          i sökmotorer och delningsförhandsvisningar. Du behåller äganderätten till ditt material.
        </p>
      </Section>

      <Section title="4. AI-funktioner, prisförslag och videoskanning">
        <p>
          Sellavo använder AI för att granska bilder, föreslå rubrik, annonstext, kategori och pris,
          för att rensa bakgrunden på bilder och för att skanna av en film och lista prylar med ett
          uppskattat andrahandsvärde. Allt sådant resultat är{" "}
          <span className="text-foreground">vägledande förslag</span> — inte en värdering, ett
          intyg, en garanti eller rådgivning.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            AI:n kan missa saker, hitta på detaljer, känna igen fel modell eller märke och
            uppskatta ett pris som är för högt eller för lågt.
          </li>
          <li>
            Videoskanningen är en grov genomgång av filmen. Antal prylar, beskrivningar och värden
            kan bli fel, ofullständiga eller dubblerade.
          </li>
          <li>
            Bilder där bakgrunden rensats är AI-bearbetade. Du ansvarar för att bilden fortfarande
            visar prylen rättvisande innan du publicerar den.
          </li>
        </ul>
        <p>
          Det är alltid du som användare som ansvarar för annonsens text, bilder, uppgifter och
          pris. Läs igenom, ändra och kontrollera allt AI:n föreslår innan du publicerar eller
          säljer. Vi lämnar inga garantier för att ett föreslaget pris går att få, och vi ansvarar
          inte för affärer, förluster eller uteblivna vinster som beror på att du följt ett
          AI-förslag. Funktionerna kan begränsas, ändras eller stängas av när som helst.
        </p>
        <p>
          Filmen du skannar sparas inte hos oss efter att skanningen är klar. Listan med prylar
          sparas under "Mina prylar" tills du raderar den, tillsammans med en liten stillbild från
          filmen för varje pryl så att du känner igen den. Stillbilderna raderas automatiskt efter
          tolv månader.
        </p>

      </Section>

      <Section title="5. Abonnemang, gränser och rimlig användning">
        <p>
          Vad varje konto får göra framgår på{" "}
          <span className="text-foreground">prissidan</span>. Gränserna finns för att tjänsten ska
          fungera bra för alla och användas som den är tänkt.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <span className="text-foreground">Många annonser</span> (Pro 200 per månad, Expert 500
            per månad) har också dygns- och veckospärrar: Pro 25 per dygn och 100 per vecka, Expert
            40 per dygn och 200 per vecka.
          </li>

          <li>
            <span className="text-foreground">Obegränsat med omskrivningar</span> (Pro) gäller
            omskrivningar av dina egna annonser. Automatiserad eller extrem användning får bromsas
            eller stoppas.
          </li>

          <li>
            Videoskanning, bakgrundsrensning av bilder och AI-sökningar har egna spärrar per timme
            och dygn. Aktuella nivåer står på prissidan och visas i verktyget.
          </li>
          <li>
            Vi får justera gränserna om kostnaderna eller belastningen kräver det. Väsentliga
            försämringar för ett betalande konto meddelas i förväg.
          </li>
        </ul>
        <p>
          Försöker någon kringgå gränserna — med flera konton, skript eller automatik — får vi
          bromsa, pausa eller stänga av kontot enligt punkt 7.
        </p>
      </Section>

      <Section title="6. Vännerbjudanden, poäng och belöningar">
        <p>
          Sellavo kan erbjuda dig att bjuda in vänner och ge poäng eller andra förmåner när någon du
          bjudit in registrerar sig och uppfyller de kvalificeringskrav som gäller för tillfället.
          Poäng och belöningar är en <span className="text-foreground">bonus inom tjänsten</span>{" "}
          och inte en betalning från Sellavo till dig.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <span className="text-foreground">Inget penningvärde.</span> Poäng motsvarar inte
            riktiga pengar, krediter eller någon annan form av tillgång. De kan inte lösas in mot
            kontanter, överföras till ett bankkonto eller användas utanför Sellavo.
          </li>
          <li>
            <span className="text-foreground">Ingen ersättning.</span> Du har aldrig rätt till
            ersättning, återbetalning eller kompensation för poäng som inte har använts, poäng som
            har förverkats eller förändringar i programmet.
          </li>
          <li>
            <span className="text-foreground">Personligt och icke-överlåtbara.</span> Poäng är
            knutna till ditt konto och får inte säljas, bytas, överlåtas eller överföras till ett
            annat konto.
          </li>
          <li>
            <span className="text-foreground">Vi ändrar reglerna.</span> Vi förbehåller oss rätten
            att när som helst ändra hur poäng intjänas, hur mycket de är värda, vilka belöningar som
            finns och hur de kan användas. Vi kan också pausa eller avsluta programmet.
          </li>
          <li>
            <span className="text-foreground">Missbruk.</span> Vi får neka, återkalla eller
            förverka poäng och belöningar om vi misstänker missbruk, till exempel fejkade konton,
            upprepade registreringar från samma enhet eller IP-adress, eller andra försök att
            kringgå reglerna.
          </li>
          <li>
            <span className="text-foreground">Kvalificerade inbjudningar.</span> En inbjudan ger
            endast poäng om den som bjuds in registrerar sig med din kod, bekräftar sin e-postadress
            och uppfyller de aktivitetskrav vi ställer. Vi avgör om en inbjudan är kvalificerad.
          </li>
        </ul>
        <p>
          Exempel på belöningar som visas i tjänsten — till exempel ”3 poäng ger en gratis Plus-månad”
          — är vägledande och gäller bara så länge den aktuella belöningen finns tillgänglig. Du kan
          inte kräva en belöning som har utgått eller bytts ut.
        </p>
      </Section>

      <Section title="7. Vår rätt att moderera, ta bort och stänga av">
        <p>
          Vi vill hålla Sellavo tryggt och seriöst. Vi förbehåller oss därför rätten att, när som
          helst och utan föregående varsel, vidta någon eller flera av följande åtgärder:
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li>granska, dölja, avpublicera, redigera eller radera en annons, bild eller text</li>
          <li>
            neka publicering av innehåll vi bedömer som olämpligt, olagligt, missvisande,
            felkategoriserat eller skadligt för tjänstens rykte
          </li>
          <li>flytta en annons till rätt kategori eller korrigera uppenbara fel</li>
          <li>begränsa antalet annonser ett konto kan publicera</li>
          <li>tillfälligt stänga av, blockera eller permanent avsluta ett konto</li>
          <li>neka en avstängd användare att skapa nytt konto</li>
          <li>
            lämna uppgifter till polis eller annan myndighet vid misstanke om brott eller på
            myndighets begäran
          </li>
        </ul>
        <p>
          Åtgärder kan vidtas vid överträdelse av dessa villkor eller lag, vid befogad misstanke om
          bedrägeri eller missbruk, efter anmälningar från andra användare, eller om innehållet på
          annat sätt är olämpligt. Vi gör en egen bedömning och är inte skyldiga att förhandsgranska
          allt innehåll. Vi är inte ersättningsskyldiga för att en annons tagits bort eller ett
          konto stängts av, och borttaget innehåll kan inte alltid återskapas.
        </p>
        <p>
          Anser du att en åtgärd är felaktig kan du begära omprövning genom att skriva till{" "}
          <span className="text-foreground">{CONTACT}</span>. Misstänker du att en annons bryter mot
          villkoren, anmäl den till samma adress.
        </p>
      </Section>

      <Section title="7. Tillgänglighet och ansvar">
        <p>
          Tjänsten tillhandahålls i befintligt skick. Vi strävar efter hög tillgänglighet men kan
          inte garantera att den alltid är felfri eller tillgänglig, och vi ansvarar inte för
          indirekt skada eller utebliven vinst till följd av användningen. AI-genererade texter och
          prisförslag är just förslag — du ansvarar för att kontrollera dem innan du publicerar.
          Tvingande konsumenträttigheter påverkas inte.
        </p>
      </Section>

      <Section title="8. Ändringar av villkoren">
        <p>
          Vi kan uppdatera villkoren, till exempel vid nya funktioner eller ändrad lagstiftning. Den
          senaste versionen gäller alltid och datumet högst upp visar när den togs fram. Fortsätter
          du använda tjänsten efter en ändring accepterar du den nya versionen.
        </p>
      </Section>

      <Section title="10. Appen för iOS och Android">
        <p>
          Sellavo finns även som app. Villkoren är desamma som på webben, med dessa tillägg:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Betalda nivåer säljs inte i appen. Har du Plus eller Pro fungerar allt som vanligt i
            appen, men själva bytet av nivå görs utanför appen.
          </li>
          <li>
            Appen kräver de behörigheter du själv godkänner. Säger du nej fungerar resten av appen
            ändå.
          </li>
          <li>
            Du kan radera ditt konto direkt i appen under Mitt konto → Farozonen, utan att kontakta
            oss.
          </li>
          <li>
            Apple och Google är inte part i avtalet mellan dig och Sellavo och ansvarar inte för
            tjänsten, innehållet eller supporten.
          </li>
          <li>
            Appen får inte användas där lagstiftning hindrar det, och du intygar att du inte finns
            på någon sanktionslista.
          </li>
        </ul>
      </Section>

      <Section title="11. Avsluta">
        <p>
          Du kan när som helst radera dina annonser eller begära att kontot tas bort genom att
          kontakta <span className="text-foreground">{CONTACT}</span>. Svensk lag tillämpas på dessa
          villkor och tvister prövas av svensk domstol.
        </p>
      </Section>
    </div>
  );
}

function TranslatedLegalBody({ copy }: { copy: ReturnType<typeof getLegalCopy> }) {
  return <div>
    <p className="text-xs text-muted-foreground">{copy.updated}</p>
    {copy.sections.map((section) => <Section key={section.title} title={section.title}>
      {section.paragraphs?.map((text) => <p key={text}>{text}</p>)}
      {section.bullets ? <ul className="ml-4 list-disc space-y-1">{section.bullets.map((text) => <li key={text}>{text}</li>)}</ul> : null}
    </Section>)}
  </div>;
}

const META: Record<
  Exclude<LegalDoc, null>,
  { title: string; description: string; body: () => ReactNode }
> = {
  integritet: {
    title: "Integritetspolicy",
    description: "Så behandlar Sellavo dina personuppgifter enligt GDPR.",
    body: PrivacyPolicy,
  },
  cookies: {
    title: "Cookiepolicy",
    description: "Vilka cookies vi använder och hur du styr dem.",
    body: CookiePolicy,
  },
  villkor: {
    title: "Användarvillkor",
    description: "Reglerna för att använda Sellavo och publicera annonser.",
    body: TermsOfUse,
  },
  regler: {
    title: "Annonsregler",
    description: "Reglerna som gäller för annonser, bilder, kontakt och auktioner.",
    body: () => <AdRulesContent compact />,
  },
};

export function LegalModal({
  doc,
  onClose,
  onBack,
}: {
  doc: LegalDoc;
  onClose: () => void;
  /** Visas som en "Tillbaka"-knapp och används i stället för stängning. */
  onBack?: () => void;
}) {
  const { lang } = useLocale();
  const translatedMeta = lang === "sv" ? null : {
    integritet: getLegalCopy(lang, "privacy"), cookies: getLegalCopy(lang, "cookies"), villkor: getLegalCopy(lang, "terms"),
    regler: { title: lang === "en" ? "Listing rules" : "Reglas de anuncios", description: lang === "en" ? "Rules for listings, images, contact and auctions." : "Reglas para anuncios, imágenes, contacto y subastas." },
  };
  const meta = doc ? { ...META[doc], ...(translatedMeta?.[doc] ?? {}) } : null;
  const Body = meta?.body;
  return (
    <Dialog open={doc !== null} onOpenChange={(v) => !v && (onBack ? onBack() : onClose())}>
      <DialogContent className="max-h-[85vh] max-w-2xl">
        <DialogHeader>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-1 mb-1 inline-flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
               <ChevronLeft className="h-3.5 w-3.5" /> {lang === "en" ? "Back" : lang === "es" ? "Volver" : "Tillbaka"}
            </button>
          ) : null}
          <DialogTitle className="font-heading text-xl">{meta?.title}</DialogTitle>
          <DialogDescription>{meta?.description}</DialogDescription>
        </DialogHeader>
        {/* Negativ marginal drar scrollisten ut mot modalens kant i stället för
            att den ska ligga långt in på texten. */}
        <ScrollArea className="-mr-4 max-h-[60vh] pr-4 sm:-mr-5 sm:pr-5">
          {Body ? <Body /> : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
